/**
 * Admin password policy (admin SPA mirror of
 * `packages/api/src/modules/admin/utils/admin-password-policy.ts`).
 *
 * Single source of truth for the password-strength rules applied at the
 * two password-CHOOSING surfaces in the admin SPA:
 *
 *   1. Invite-accept (`SetPasswordPage`) — admin sets initial password
 *      after clicking the invite link.
 *   2. Password-reset confirm (`ResetConfirmPage`) — admin rotates after
 *      receiving the reset email.
 *
 * The LoginStep does NOT use this util: when entering an existing
 * password the user shouldn't get policy hints (we don't know what the
 * server-stored password is, and showing "needs 12 chars" on a 9-char
 * legitimate-but-old password would confuse). Login surfaces 401
 * `admin_invalid_credentials` and stops.
 *
 * Wire-stable reason discriminators MUST match the backend's
 * `PasswordPolicyViolation` union — the i18n layer in `SetPasswordPage`
 * / `ResetConfirmPage` resolves each reason to a translated string.
 *
 * BR15 policy (v1):
 *   - MIN_LENGTH = 12 characters
 *   - At least one letter (a-z OR A-Z)
 *   - At least one digit (0-9)
 *   - <= 72 bytes when encoded as UTF-8 (bcrypt's hard ceiling)
 *
 * Order of checks (matches backend exactly so the SPA's inline preview
 * matches the server's 422 response branch):
 *
 *   too_long_for_bcrypt  →  too_short  →  no_letter  →  no_digit
 */

const MIN_LENGTH = 12;
const BCRYPT_MAX_BYTES = 72;

/**
 * Wire-stable reason discriminators. Must mirror the backend's
 * `PasswordPolicyViolation` union.
 */
export type PasswordPolicyViolation =
  | 'too_short'
  | 'no_letter'
  | 'no_digit'
  | 'too_long_for_bcrypt';

export type PasswordPolicyResult = { ok: true } | { ok: false; reason: PasswordPolicyViolation };

/**
 * UTF-8 byte length helper. The browser has no `Buffer.byteLength` —
 * `TextEncoder` is the standards-aligned equivalent and lives in every
 * browser the admin panel targets (modern evergreen).
 */
const utf8ByteLength = (s: string): number => new TextEncoder().encode(s).length;

/**
 * Validate a plaintext password against the policy. Pure function;
 * callers compose with their own error-throwing or React state updates.
 */
export function validatePassword(plain: string): PasswordPolicyResult {
  // Bcrypt's 72-byte hard ceiling. Checked first because a >72-byte
  // password is a hard fail regardless of any other property.
  if (utf8ByteLength(plain) > BCRYPT_MAX_BYTES) {
    return { ok: false, reason: 'too_long_for_bcrypt' };
  }
  if (plain.length < MIN_LENGTH) {
    return { ok: false, reason: 'too_short' };
  }
  if (!/[a-zA-Z]/.test(plain)) {
    return { ok: false, reason: 'no_letter' };
  }
  if (!/[0-9]/.test(plain)) {
    return { ok: false, reason: 'no_digit' };
  }
  return { ok: true };
}

/**
 * Map a `PasswordPolicyViolation` reason → i18n key under the
 * `auth.invite.setPassword.policy` namespace (shared by ResetConfirmPage).
 *
 * Callers may also pass the special `'mismatch'` discriminator when the
 * confirm-password field doesn't match — surfaced inline, never coming
 * from the server.
 */
export type PasswordIssue = PasswordPolicyViolation | 'mismatch';

export function policyI18nKey(issue: PasswordIssue): string {
  switch (issue) {
    case 'too_short':
      return 'auth.invite.setPassword.policy.tooShort';
    case 'no_letter':
      return 'auth.invite.setPassword.policy.noLetter';
    case 'no_digit':
      return 'auth.invite.setPassword.policy.noDigit';
    case 'too_long_for_bcrypt':
      return 'auth.invite.setPassword.policy.tooLong';
    case 'mismatch':
      return 'auth.invite.setPassword.policy.mismatch';
    default: {
      const _exhaustive: never = issue;
      void _exhaustive;
      return 'auth.errors.unknown';
    }
  }
}
